// Shared "update status with a date, kept as history" engine for the ERP
// business screens. Every status-driven record (CRM lead stage, Sales order
// status, Production order status, …) can use these helpers so the behaviour
// and look are identical everywhere.
//
//   const next = withStatusChange(record, { status, date, note }, 'stage');
//   <StatusDialog open record options={STAGES} statusField="stage" ... />
//   <StatusTimeline record statusField="stage" toneFor={(s) => stageTone[s]} />
import React from 'react';
import {
  Box, Collapse, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import { Clock } from 'lucide-react';
import { StatusChip, FormDialog, DIALOG_PAPER_SX } from './businessUi';

export { Collapse };

// Today as YYYY-MM-DD (browser-local) — the default for a new status entry.
export const todayStr = () => new Date().toISOString().slice(0, 10);

// The status-change history for a record. If none exists yet, seed an initial
// "Created" entry from the record's own status + date so the timeline always
// starts from where the record began.
export function getHistory(record, statusField = 'status') {
  if (record.history && record.history.length) return record.history;
  if (record[statusField]) return [{ status: record[statusField], date: record.date || '', note: 'Created' }];
  return [];
}

// A new record with the status changed and the change appended to its history.
export function withStatusChange(record, { status, date, note }, statusField = 'status') {
  const history = [...getHistory(record, statusField), { status, date, note: note || '' }];
  return { ...record, [statusField]: status, history };
}

// Dialog to record a status change: pick the new status, the date it happened,
// and an optional note. `onSubmit` receives { status, date, note }.
export function StatusDialog({ open, record, label = 'Status', statusField = 'status', options, onClose, onSubmit, title }) {
  const fields = [
    { name: 'status', label: `New ${label}`, type: 'select', default: record ? record[statusField] : '', required: true, options },
    { name: 'date', label: `${label} Date`, type: 'date', default: todayStr(), required: true },
    { name: 'note', label: 'Note (optional)', helper: 'Reason / reference (e.g. PO no., remark)' },
  ];
  return (
    <FormDialog
      open={open}
      title={title || `Update ${label}`}
      fields={fields}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel="Update Status"
    />
  );
}

// Popup showing a record's full dated status history — opened by clicking a row.
export function StatusHistoryDialog({ open, record, statusField = 'status', toneFor, title, subtitle, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: DIALOG_PAPER_SX }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 18, pb: subtitle ? 0.5 : 2 }}>
        {title || 'Status History'}
        {subtitle && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 400 }}>{subtitle}</Typography>}
      </DialogTitle>
      <DialogContent>
        {record && <StatusTimeline record={record} statusField={statusField} toneFor={toneFor} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// Inline, dated timeline of every status change — rendered inside an expanded row.
export function StatusTimeline({ record, statusField = 'status', toneFor }) {
  const history = getHistory(record, statusField);
  return (
    <Box sx={{ py: 1.5, px: 1, bgcolor: 'background.default', borderRadius: 1.5, my: 1 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', mb: 0.75 }}>
        Status History
      </Typography>
      {history.length === 0 && <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>No history yet.</Typography>}
      {history.map((h, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.6, borderBottom: i < history.length - 1 ? '1px solid #eef2f7' : 'none' }}>
          <Clock size={14} color="#7c5cff" />
          <Typography sx={{ fontSize: 12.5, color: 'text.primary', minWidth: 100 }}>{h.date || '—'}</Typography>
          <StatusChip label={h.status} tone={toneFor ? toneFor(h.status) : 'default'} />
          <Typography sx={{ fontSize: 12, color: 'text.disabled', flex: 1 }}>{h.note || ''}</Typography>
        </Box>
      ))}
    </Box>
  );
}
